import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const parseUrlNum = (params: URLSearchParams, key: string): number | undefined => {
  const val = params.get(key);
  if (val === null) return undefined;
  const num = Number(val);
  return Number.isNaN(num) ? undefined : num;
};

export const parseUrlDate = (params: URLSearchParams, key: string): Date | null => {
  const val = params.get(key);
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const toDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

type UpdateParams = (updater: (prev: URLSearchParams) => URLSearchParams) => void;

export const useUrlParams = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  // Always read window.location.search — history.replaceState updates it synchronously,
  // so chained setter calls within the same tick all see the latest URL.
  const updateParams: UpdateParams = useCallback(
    (updater) => {
      const prev = new URLSearchParams(window.location.search);
      const next = updater(prev);
      if (prev.toString() === next.toString()) return;
      navigate({ search: next.toString() }, { replace: true });
    },
    [navigate]
  );

  const makeNumSetter = useCallback(
    (key: string): Dispatch<SetStateAction<number | undefined>> =>
      (action) => {
        updateParams((prev) => {
          const next = new URLSearchParams(prev);
          const val = typeof action === "function" ? action(parseUrlNum(prev, key)) : action;
          if (val === undefined) next.delete(key);
          else next.set(key, String(val));
          return next;
        });
      },
    [updateParams]
  );

  const makeDateSetter = useCallback(
    (key: string): Dispatch<SetStateAction<Date | null>> =>
      (action) => {
        updateParams((prev) => {
          const next = new URLSearchParams(prev);
          const val = typeof action === "function" ? action(parseUrlDate(prev, key)) : action;
          if (!val) next.delete(key);
          else next.set(key, toDateStr(val));
          return next;
        });
      },
    [updateParams]
  );

  const makeStrSetter = useCallback(
    <T extends string>(key: string, defaultVal: T): Dispatch<SetStateAction<T>> =>
      (action) => {
        updateParams((prev) => {
          const next = new URLSearchParams(prev);
          const current = (prev.get(key) as T) || defaultVal;
          const val = typeof action === "function" ? action(current) : action;
          if (val === defaultVal) next.delete(key);
          else next.set(key, val);
          return next;
        });
      },
    [updateParams]
  );

  // useLocation().search triggers re-render when URL changes (reactive read)
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  return { searchParams, makeNumSetter, makeDateSetter, makeStrSetter };
};
